import React from 'react';
import PropTypes from 'prop-types';

class ProgressIndicator extends React.Component {
  handleStepClick = (stepNum) => {
    // Get the path for the clicked step
    const { steps, onStepClick } = this.props;
    
    // If onStepClick is provided, use it
    if (onStepClick) {
      onStepClick(stepNum);
      return;
    }
    
    // Otherwise, use our localStorage persistence and redirect
    const stepPath = this.getStepPath(stepNum);
    if (stepPath) {
      // Save the step to localStorage
      localStorage.setItem('findbob_onboarding_step', stepNum);
      
      // Navigate to the step
      window.location.href = `/onboarding/${stepPath}`;
    }
  }
  
  getStepPath = (stepNum) => {
    // Map step numbers to paths
    const pathMap = {
      1: 'contact_information',
      2: 'motivation',
      3: 'production_information',
      4: 'practice_information',
      5: 'headline_and_about_me'
    };
    
    return pathMap[stepNum] || '';
  }
  
  render() {
    const { steps, currentStep, totalSteps } = this.props;
    
    // Generate default step names if not provided
    const stepNames = steps || Array(totalSteps).fill().map((_, i) => `Step ${i + 1}`);
    
    // Calculate progress percentage
    const progressPercentage = ((currentStep - 1) / (stepNames.length - 1)) * 100;
    
    return (
      <div className="pb-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="w-full bg-succession-bg-secondary rounded-full h-2.5 overflow-hidden shadow-inner">
            <div 
              className="bg-succession-primary h-2.5 rounded-full transition-all duration-500 ease-in-out shadow-sm" 
              style={{ 
                width: `${progressPercentage}%`,
                backgroundColor: 'var(--succession-primary, #5489B6)'
              }}
            ></div>
          </div>
        </div>

        {/* Step Indicators */}
        <div className="relative">
          <nav aria-label="Progress">
            <ol className="flex items-center w-full">
              {stepNames.map((stepName, index) => {
                const stepNum = index + 1;
                const isActive = stepNum === currentStep;
                const isCompleted = stepNum < currentStep;
                const isLast = index === stepNames.length - 1;
                
                return (
                  <li key={`step-${stepNum}`} className={`relative flex flex-col items-center ${isLast ? '' : 'flex-1'}`}>
                    {/* Connector Line */}
                    {!isLast && (
                      <div 
                        className={`absolute top-5 left-1/2 h-0.5 w-full -translate-x-1/2 ${
                          stepNum < currentStep ? 'bg-succession-primary' : 'bg-succession-border'
                        }`}
                        style={{ 
                          left: '50%', 
                          width: 'calc(100% - 2.5rem)',
                          backgroundColor: stepNum < currentStep ? 'var(--succession-primary, #5489B6)' : 'var(--succession-border, #e5e7eb)'
                        }}
                      ></div>
                    )}
                    
                    {/* Step Circle */}
                    <button
                      onClick={() => isCompleted && this.handleStepClick(stepNum)}
                      disabled={!isCompleted && !isActive}
                      className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full 
                        transition-all duration-200 ease-in-out border-2
                        ${isActive ? 'bg-succession-primary border-succession-primary ring-4 ring-succession-primary' : ''}
                        ${isCompleted ? 'bg-succession-primary border-succession-primary hover:scale-110' : 'bg-succession-bg-primary border-succession-border'} 
                        ${(!isCompleted && !isActive) ? 'cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      style={isActive || isCompleted ? { 
                        '--tw-ring-color': 'var(--succession-primary-10, rgba(84, 137, 182, 0.1))',
                        backgroundColor: 'var(--succession-primary, #5489B6)',
                        borderColor: 'var(--succession-primary, #5489B6)',
                        '--hover-bg': 'var(--succession-primary-hover, rgba(84, 137, 182, 0.9))'
                      } : {
                        backgroundColor: 'var(--succession-bg-primary, #ffffff)',
                        borderColor: 'var(--succession-border, #e5e7eb)'
                      }}
                      onMouseEnter={isCompleted ? (e) => {
                        e.target.style.backgroundColor = 'var(--succession-primary-hover, rgba(84, 137, 182, 0.9))';
                      } : undefined}
                      onMouseLeave={isCompleted ? (e) => {
                        e.target.style.backgroundColor = 'var(--succession-primary, #5489B6)';
                      } : undefined}
                      aria-current={isActive ? 'step' : undefined}
                      title={isCompleted ? `Go back to ${stepName}` : stepName}
                    >
                      {isCompleted ? (
                        <svg 
                          className="h-5 w-5 text-white" 
                          style={{ color: '#ffffff' }}
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 20 20" 
                          fill="currentColor" 
                          aria-hidden="true"
                        >
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                                                  <span 
                            className="text-sm font-semibold"
                            style={{
                              color: isActive ? '#000000' : 'var(--succession-text-secondary, #6b7280)',
                              fontWeight: '600',
                              zIndex: 10,
                              position: 'relative'
                            }}
                          >
                          {stepNum}
                        </span>
                      )}
                    </button>
                    
                    {/* Step Label */}
                    <div className="mt-3 h-10 flex items-start justify-center">
                      <span 
                        className={`text-xs font-medium text-center w-24 md:text-sm md:w-28 
                          ${isActive ? 'text-succession-primary font-bold' : ''}
                          ${isCompleted ? 'text-succession-primary' : 'text-succession-text-muted'}
                        `}
                        style={isActive || isCompleted ? {
                          color: 'var(--succession-primary, #5489B6)'
                        } : {
                          color: 'var(--succession-text-muted, #9ca3af)'
                        }}
                      >
                        {stepName}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    );
  }
}

ProgressIndicator.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string),
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  onStepClick: PropTypes.func
};

ProgressIndicator.defaultProps = {
  totalSteps: 5, // Default to 5 steps
  onStepClick: null
};

export default ProgressIndicator; 